import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import Quagga from 'quagga';
import { IoClose, IoCloseSharp } from 'react-icons/io5';
import { CgSpinner } from 'react-icons/cg';
import { useApiLoading } from 'react-reqq-lite';

import { showModal } from 'modules/common/modal';
import { Button, FormInput } from 'modules/common/input';
import PageLoader from 'modules/common/components/PageLoader';
import ErrorLabel from 'modules/common/components/ErrorLabel';
import { toastWarning } from 'modules/common/toast';

const INIT_FORM = {
  sku: '',
};

function Scanner({
  onDetected,
  stopOnDetect,
  withSku,
  onInputChange,
  inputClassName,
  error,
  withSkuPreview,
  withLightingDescription,
  constraints,
  withPageLoader,
  isLoading,
  onStop,
  isLoadingKey,
  isLoadingMethod,
  setIsCameraLoading,
  onClose,
}) {
  const [form, setForm] = React.useState(INIT_FORM);
  const [skuPreview, setSkuPreview] = React.useState();
  const [isQuaggaLoading, setIsQuaggaLoading] = React.useState(true);

  const isApiLoading = useApiLoading(isLoadingKey, isLoadingMethod);

  const handleOnSubmit = (e) => {
    e.preventDefault();

    if (isLoading || isApiLoading) {
      onStop(Quagga);
      return;
    }

    setSkuPreview('');
    onDetected(form?.sku, Quagga);
  };

  const handleOnChange = (v) => {
    onInputChange((prevState) => ({
      ...prevState,
      ...v(),
    }));
    setForm(v);
  };

  const handleOnStop = () => {
    onStop?.(Quagga);
  };

  React.useEffect(() => {
    Quagga.init(
      {
        inputStream: {
          name: 'Live',
          type: 'LiveStream',
          target: document.querySelector('#quaggas-scanner'), // Or '#yourElement' (optional)
          constraints: {
            facingMode: 'environment',
            ...constraints,
          },
        },
        locator: {
          patchSize: 'medium',
          halfSample: true,
        },
        numOfWorkers: 2,
        frequency: 10,
        decoder: {
          readers: ['ean_reader'],
        },
        locate: true,
      },
      (err) => {
        if (err) {
          // eslint-disable-next-line no-console
          console.log(err, 'scanner failed to initialize.');
        }
        try {
          Quagga?.start();
        } catch ($err) {
          toastWarning('Please allow the camera to access this feature.');
          // eslint-disable-next-line
          console.error($err);
          Quagga.stop();
        }
        setIsQuaggaLoading(false);

        return () => {
          Quagga.stop();
        };
      }
    );

    // //detecting boxes on stream
    Quagga.onProcessed((result) => {
      const drawingCtx = Quagga.canvas.ctx.overlay;
      const drawingCanvas = Quagga.canvas.dom.overlay;

      if (result) {
        if (result.boxes) {
          drawingCtx.clearRect(
            0,
            0,
            Number(drawingCanvas.getAttribute('width')),
            Number(drawingCanvas.getAttribute('height'))
          );
          result.boxes
            .filter((box) => box !== result.box)
            .forEach((box) => {
              Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
                color: 'green',
                lineWidth: 2,
              });
            });
        }

        if (result.box) {
          Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
            color: '#00F',
            lineWidth: 2,
          });
        }

        if (result.codeResult && result.codeResult.code) {
          Quagga.ImageDebug.drawPath(
            result.line,
            { x: 'x', y: 'y' },
            drawingCtx,
            { color: 'red', lineWidth: 3 }
          );
        }
      }
    });

    Quagga.onDetected((result) => {
      const { code } = result.codeResult || {};

      if (code) {
        if (withSkuPreview) setSkuPreview(code);
        setForm((prevState) => ({ ...prevState, sku: '' }));
      }
      onDetected(code, Quagga);

      if (stopOnDetect) {
        Quagga.stop();
      }
    });

    return () => Quagga.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setIsCameraLoading(isQuaggaLoading);
  }, [isQuaggaLoading, setIsCameraLoading]);

  return (
    <>
      {isQuaggaLoading && withPageLoader && (
        <div className="bg-white">
          <PageLoader width="190px" />
        </div>
      )}

      <div className="aspect-auto relative overflow-hidden">
        {!isQuaggaLoading && (
          <button
            className="absolute top-2 right-2 bg-black opacity-70 p-0.5 rounded-full z-10"
            onClick={() => onClose(false)}
            type="button"
          >
            <IoClose className="h-4 w-4 text-white" />
          </button>
        )}

        {withLightingDescription && !isQuaggaLoading && (
          <div className="p-1.5 py-0.5 rounded-md bg-black bg-opacity-60 text-white font-light text-xxs absolute top-2 left-1/2 -translate-x-1/2 truncate">
            Please, ensure good lighting for accurate measure
          </div>
        )}

        <div
          id="quaggas-scanner"
          className={cn('rounded-md overflow-hidden relative max-h-72', {
            'mb-2.5': !isQuaggaLoading,
            hidden: isQuaggaLoading,
          })}
        >
          <div id="quaggas-scanning" />
        </div>

        {skuPreview && withSkuPreview && !isQuaggaLoading && (
          <div className="p-2 rounded-md bg-black bg-opacity-60 text-white uppercase font-semibold text-sm absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {(isLoading || isApiLoading) && (
              <CgSpinner className="h-4 w-4 text-gray-300 animate-spin" />
            )}
            <div>{skuPreview}.</div>
            {(isLoading || isApiLoading) && (
              <Button
                label="Stop"
                className="font-semibold hover:text-primary-300"
                onClick={handleOnStop}
              />
            )}
          </div>
        )}
      </div>

      <div>
        {error && (
          <ErrorLabel
            error={error}
            errorClassName="!rounded-none py-px pt-0.5 border-primary-100 border-opacity-40 !text-xxs"
          />
        )}
      </div>

      {withSku && !isQuaggaLoading && (
        <div className="p-1.5">
          <form onSubmit={handleOnSubmit} className="flex">
            <div className="border rounded-l overflow-hidden flex flex-1">
              <div className="flex-1">
                <FormInput
                  onChange={handleOnChange}
                  name="sku"
                  label=""
                  value={form?.sku}
                  className={cn(
                    'rounded-none !border-form-inactive !border-0 py-2',
                    inputClassName
                  )}
                  placeholder="SKU"
                />
              </div>
              {(isLoading || isApiLoading) && form?.sku && (
                <div className="bg-white self-stretch flex pr-2">
                  <CgSpinner className="h-4 w-4 text-gray-300 animate-spin my-auto" />
                </div>
              )}
            </div>

            <div>
              <Button
                label={
                  (isLoading || isApiLoading) && form?.sku ? 'Stop' : 'Confirm'
                }
                className={cn(
                  'btn !px-3 !h-[38px] !rounded !rounded-l-none font-medium cursor-pointer !border-l-0 text-xs sm',
                  (isLoading || isApiLoading) && form?.sku
                    ? 'gray !bg-gray-100 !border !border-gray-200 !border-l-0'
                    : 'primary'
                )}
                type="submit"
                disabled={form?.sku?.length <= 0}
              />
            </div>
          </form>
        </div>
      )}
    </>
  );
}

Scanner.defaultProps = {
  onDetected: () => {},
  onInputChange: () => {},
  onStop: () => {},
  constraints: {},
  stopOnDetect: true,
  withSkuPreview: false,
  withPageLoader: false,
  withLightingDescription: false,
  isLoading: false,
  withSku: false,
  inputClassName: '',
  error: '',
  isLoadingKey: '',
  isLoadingMethod: '',
  setIsCameraLoading: () => {},
  onClose: () => {},
};

Scanner.propTypes = {
  onDetected: PropTypes.instanceOf(Function),
  setIsCameraLoading: PropTypes.instanceOf(Function),
  onClose: PropTypes.instanceOf(Function),
  inputClassName: PropTypes.string,
  withPageLoader: PropTypes.bool,
  onInputChange: PropTypes.instanceOf(Function),
  onStop: PropTypes.instanceOf(Function),
  constraints: PropTypes.instanceOf(Object),
  error: PropTypes.string,
  withLightingDescription: PropTypes.bool,
  stopOnDetect: PropTypes.bool,
  isLoading: PropTypes.bool,
  withSku: PropTypes.bool,
  isLoadingKey: PropTypes.string,
  isLoadingMethod: PropTypes.string,
  withSkuPreview: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      onStop: PropTypes.oneOfType([
        PropTypes.instanceOf(Function),
        PropTypes.bool,
      ]),
      isLoading: PropTypes.bool,
    }),
  ]),
};

export default Scanner;

export const useScanner = () => {
  const action = (name, onChange) => {
    showModal({
      focusTrap: false,
      noClose: true,
      titleClassName: 'hidden',
      withMobileFullscreen: true,
      containerClassName: '!p-0 !bg-black',
      modalSize: 'modal-sm',
      content: (onSubClose) => (
        <div className="flex flex-col flex-1 relative">
          <div className="absolute top-5 right-4 z-[11]">
            <IoCloseSharp
              className="h-6 w-6 text-white cursor-pointer"
              onClick={onSubClose}
            />
          </div>
          <div className="my-auto">
            <div className="relative">
              <Scanner
                onDetected={(result) => {
                  if (result) {
                    onChange((prevState) => ({
                      ...prevState,
                      [name]: result,
                    }));
                    onSubClose();
                  }
                }}
              />
            </div>
          </div>
        </div>
      ),
    });
  };

  return action;
};
